import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Media } from "../types/Media";
import * as api from "../services/api";
import { config } from "../config/environment";
import { log, handleApiError } from "../utils/helpers";

interface MediaFormProps {
  initialData?: Media;
}

const MediaForm: React.FC<MediaFormProps> = ({ initialData }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    author: "",
    publishDate: new Date().toISOString().split("T")[0],
    isPublished: true,
    isFeatured: false,
    order: 0,
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (id) {
      fetchMedia();
    }
  }, [id]);

  const fetchMedia = async () => {
    try {
      const response = await api.get(`/media/${id}`);
      const media = response.data;
      setFormData({
        title: media.title,
        content: media.content,
        author: media.author,
        publishDate: media.publishDate.split("T")[0],
        isPublished: media.isPublished,
        isFeatured: media.isFeatured || false,
        order: media.order || 0,
      });
      setImagePreview(api.getImageUrl(media.image));
    } catch (error) {
      console.error("Error fetching media:", error);
      handleApiError(error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("content", formData.content);
      formDataToSend.append("author", formData.author);
      formDataToSend.append("publishDate", formData.publishDate);
      formDataToSend.append("isPublished", formData.isPublished.toString());
      formDataToSend.append("isFeatured", formData.isFeatured.toString());
      formDataToSend.append("order", formData.order.toString());

      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      if (isEdit) {
        await api.put(`/media/${id}`, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        log(`Updated media: ${formData.title}`);
      } else {
        if (!imageFile) {
          setError("Gambar wajib diupload");
          setLoading(false);
          return;
        }
        await api.post("/media", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        log(`Created media: ${formData.title}`);
      }

      navigate("/media");
    } catch (error) {
      console.error("Error saving media:", error);
      handleApiError(error);
      setError("Terjadi kesalahan saat menyimpan media");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? "Edit Media" : "Tambah Media"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box mb={2}>
            <TextField
              name="title"
              label="Judul"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Box>

          <Box mb={2}>
            <TextField
              name="content"
              label="Konten"
              value={formData.content}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={8}
              variant="outlined"
              helperText="Format teks akan dipertahankan. Gunakan Enter untuk baris baru dan spasi untuk paragraf."
            />
          </Box>

          <Box mb={2}>
            <TextField
              name="author"
              label="Penulis"
              value={formData.author}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Box>

          <Box mb={2}>
            <TextField
              name="publishDate"
              label="Tanggal Publikasi"
              type="date"
              value={formData.publishDate}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          <Box mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublished}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isPublished: e.target.checked,
                    }))
                  }
                  name="isPublished"
                />
              }
              label="Terbitkan langsung"
            />
          </Box>

          <Box mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isFeatured}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isFeatured: e.target.checked,
                    }))
                  }
                  name="isFeatured"
                />
              }
              label="Jadikan berita utama (featured)"
            />
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              Hanya satu media yang bisa menjadi berita utama. Jika diaktifkan,
              media lain akan otomatis non-featured.
            </Typography>
          </Box>

          <Box mb={2}>
            <TextField
              fullWidth
              label="Urutan tampil"
              name="order"
              type="number"
              value={formData.order}
              onChange={handleChange}
              variant="outlined"
              helperText="Angka lebih kecil akan tampil lebih dulu (0, 1, 2, dst)"
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Gambar {!isEdit && "*"}
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ marginBottom: "1rem" }}
              required={!isEdit}
            />
            {imagePreview && (
              <Box mt={1}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "200px",
                    objectFit: "cover",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                />
              </Box>
            )}
          </Box>

          <Box display="flex" gap={2} mt={3}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? "Menyimpan..." : isEdit ? "Update" : "Simpan"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/media")}
              disabled={loading}
            >
              Batal
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default MediaForm;
